/**
 * Tree 组件 - 函数式版本
 * 基于 Hooks 的模块化重构
 */

import classNames from 'classnames';
import omit from 'omit.js';
import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { getNid } from '../../wui-core/src';
import CONFIG from './config';
import createStore from './createStore';
import InfiniteScroll from './infiniteScroll';
import TreeNode from './TreeNode';
import { WithConfigConsumer } from '../../wui-provider/src/context';
import { WebUI } from '../../wui-core/src';

// 导入工具函数
import {
    deepTraversal,
    handleTreeListChange,
} from './utils/dataTransform';

// 导入自定义 Hooks
import { useExpandState } from './hooks/useExpandState';
import { useCheckState } from './hooks/useCheckState';
import { useSelectState } from './hooks/useSelectState';
import { useKeyboardNav } from './hooks/useKeyboardNav';
import { useDragDrop } from './hooks/useDragDrop';

// 导入类型定义
import {
    TreeProps,
    TreeUlDomProps, RootTreeMethods, DisabledTreeNode,
    FlatTreeData, CheckedKeysObj,
    TreeData, TreeNodeProps,
    FlatTreeKeysMap
} from './iTree';

import { loopAllChildren, getSumHeight } from './utils/util';

function noop() {}

// 设置默认属性
const defaultProps = {
    clsPrefix: 'rc-tree',
    blockNode: true,
    showLine: false,
    showIcon: true,
    selectable: true,
    multiple: false,
    checkable: false,
    checkStrictly: false,
    draggable: false,
    autoExpandParent: true,
    defaultExpandAll: false,
    defaultExpandedKeys: [],
    defaultCheckedKeys: [],
    defaultSelectedKeys: [],
    onExpand: noop,
    onCheck: noop,
    onSelect: noop,
    onDragStart: noop,
    onDragEnter: noop,
    onDragOver: noop,
    onDragLeave: noop,
    onDrop: noop,
    onDragEnd: noop,
    tabIndexValue: 0,
    lazyLoad: false,
    autoSelectWhenFocus: false,
    expandOnClickNode: true,
    getCheckboxAttrs: noop,
    fieldid: '',
    syncCheckedAndSelectedStatus: false,
    disabled: false,
    icon: null,
    inverse: false,
    fieldNames: { title: "title", key: "key", children: "children" },
    dragDelayExpandTime: 500
};

const TreeFunctional: React.FC<TreeProps> = (props) => {
    // 合并默认值和传入的props
    const mergedProps = { ...defaultProps, ...props };
    // 解构 props
    const {
        clsPrefix,
        blockNode,
        showLine,
        showIcon,
        selectable,
        multiple,
        checkable,
        checkStrictly,
        draggable,
        showDraggableIcon,
        autoExpandParent,
        defaultExpandAll,
        defaultExpandedKeys,
        defaultCheckedKeys,
        defaultSelectedKeys,
        onExpand,
        onCheck,
        onSelect,
        onDragStart,
        onDragEnter,
        onDragOver,
        onDragLeave,
        onDrop,
        onDragEnd,
        tabIndexValue,
        lazyLoad,
        autoSelectWhenFocus,
        getScrollContainer,
        expandOnClickNode,
        getCheckboxAttrs,
        fieldid,
        syncCheckedAndSelectedStatus,
        disabled,
        icon,
        inverse,
        fieldNames,
        dragDelayExpandTime,
        children,
        treeData: propTreeData,
        loadData,
        onLoad,
        onDoubleClick,
        onMouseEnter,
        onMouseLeave,
        onRightClick,
        onKeyDown,
        keyFun,
        onFocus,
        renderTitle,
        renderTreeNodes,
        openIcon,
        closeIcon,
        focusable,
        mustExpandable,
        canCloseFreely,
        _getTreeObj,
        _treeNodesStates,
        unsetDropPositionX,
        dragInitialState,
        expandedKeys: controlledExpandedKeys,
        checkedKeys: controlledCheckedKeys,
        selectedKeys: controlledSelectedKeys,
        loadedKeys: controlledLoadedKeys,
        height,
        debounceDuration,
        dir,
        className,
        style,
        ...otherProps
    } = mergedProps;

    // ==================== State ====================
    const [treeData, setTreeData] = useState<TreeData[]>([]);
    const [flatTreeData, setFlatTreeData] = useState<FlatTreeData[]>([]);
    // const [prevProps, setPrevProps] = useState<TreeProps | null>(null);

    // ==================== Refs ====================
    const treeRef = useRef<HTMLUListElement | null>(null);
    const storeRef = useRef(createStore({ rowHeight: 28 }));
    const flatTreeKeysMapRef = useRef<FlatTreeKeysMap>({});
    const startIndexRef = useRef<number>(0);
    const endIndexRef = useRef<number>(CONFIG.loadBuffer ? CONFIG.defaultRowsInView + CONFIG.loadBuffer * 2 : 16);
    const cacheTreeNodesRef = useRef<React.ReactElement[]>([]);
    const disabledTreeNodesRef = useRef<DisabledTreeNode[]>([]);
    const hasCalculateRowHeightRef = useRef<boolean>(false);
    const flatKeyTreeRef = useRef<string[]>([]);
    const treeElsObjRef = useRef<{[key: string]: React.ReactElement}>({});

    // ==================== Custom Hooks ====================
    // 展开状态管理
    const {
        expandedKeys,
        loadedKeys,
        // cacheExpandedKeys,
        autoExpandKeys,
        handleExpand: hookHandleExpand,
        setExpandedKeys,
        // setLoadedKeys,
        // getFilterExpandedKeys,
        getRawExpandedKeys,
        getExpandedKeys: getExpandedKeysForDrag,
        isFold
    } = useExpandState({
        defaultExpandedKeys,
        expandedKeys: controlledExpandedKeys,
        controlled: !!('expandedKeys' in props),
        defaultExpandAll,
        autoExpandParent,
        onExpand,
        children,
        treeData: propTreeData,
        canCloseFreely,
        loadData,
        onLoad,
        fieldNames
    });

    // 勾选状态管理
    const {
        checkedKeys,
        halfCheckedKeys,
        treeNodesStates,
        // checkKeys,
        handleCheck: hookHandleCheck,
        // setCheckedKeys,
        // getCheckedKeys: getFilteredCheckedKeys,
        initTreeNodesStates
    } = useCheckState({
        defaultCheckedKeys,
        checkedKeys: controlledCheckedKeys,
        controlled: !!('checkedKeys' in props),
        checkStrictly,
        checkable,
        onCheck,
        expandedKeys,
        children,
        treeData: propTreeData,
        lazyLoad,
        disabled,
        strictRangeHasFolded: mergedProps.strictRangeHasFolded
    });

    // 选中状态管理
    const {
        selectedKeys,
        focusKey,
        handleSelect: hookHandleSelect,
        // setSelectedKeys,
        setFocusKey
    } = useSelectState({
        defaultSelectedKeys,
        selectedKeys: controlledSelectedKeys,
        controlled: !!('selectedKeys' in props),
        multiple,
        selectable,
        cancelUnSelect: props.cancelUnSelect,
        onSelect,
        children,
        // cacheTreeNodes: cacheTreeNodesRef.current, // onselect 回调使用 直接使用treeNode.props.cacheTreeNodes 解决
        syncCheckedAndSelectedStatus,
        checkedKeys,
        treeData: propTreeData,
        checkStrictly
    });

    // ==================== 性能优化：使用 Set 替代 indexOf 查找 ====================
    // 将 keys 数组转换为 Set，查找复杂度从 O(m) 降低到 O(1)
    const checkedKeysSet = useMemo(() => new Set(checkedKeys), [checkedKeys]);
    const halfCheckedKeysSet = useMemo(() => new Set(halfCheckedKeys), [halfCheckedKeys]);
    const expandedKeysSet = useMemo(() => new Set(expandedKeys), [expandedKeys]);
    const selectedKeysSet = useMemo(() => new Set(selectedKeys), [selectedKeys]);

    // ==================== showLine 优化 ====================
    // 使用 useMemo 缓存 treeNodesStatesKeysInfo 的计算，避免每次渲染重复计算
    const treeNodesStatesKeysInfo = useMemo(() => {
        if (!showLine || !treeNodesStates || typeof treeNodesStates !== 'object') {
            return {};
        }

        const keysInfo: Record<string, string> = {};
        for (let i in treeNodesStates) {
            // treeNodesStates[i].node 是 React.ReactElement，需要通过 props 访问 key
            const nodeKey = treeNodesStates[i]?.node?.props?.eventKey || treeNodesStates[i]?.node?.key;
            if (nodeKey) {
                keysInfo[nodeKey as string] = i;
            }
        }
        return keysInfo;
    }, [showLine, treeNodesStates]);

    // 拖拽功能
    const {
        dragOverNodeKey,
        // dropNodeKey,  // 暂时未使用
        dropPositionX,
        dropPositionY,
        dragNodeHighlight,
        // betweenWithKey,  // 暂时未使用
        // dragNode,  // 暂时未使用
        // dragNodesKeys,  // 暂时未使用
        handleDragStart,
        // handleDragEnterGap,  // 暂时未使用
        handleDragEnter,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handleDragEnd,
        // getDragNodes,  // 暂时未使用
        // setDragOverNodeKey  // 暂时未使用
    } = useDragDrop({
        draggable,
        dragInitialState,
        dragDelayExpandTime,
        expandedKeys,
        onDragStart,
        onDragEnter,
        onDragOver,
        onDragLeave,
        onDrop,
        onDragEnd,
        unsetDropPositionX,
        children,
        setExpandedKeys,
        getExpandedKeys: getExpandedKeysForDrag,
        getRawExpandedKeys,
        flatKeyTree: flatKeyTreeRef.current,
        treeElsObj: treeElsObjRef.current
    });

    // 键盘导航
    const {
        handleKeyDown,
        handleUlFocus
    } = useKeyboardNav({
        expandedKeys,
        selectedKeys,
        focusKey,
        clsPrefix,
        autoSelectWhenFocus,
        checkable,
        showLine,
        onExpand: hookHandleExpand,
        onSelect: hookHandleSelect,
        onCheck: checkable ? hookHandleCheck : undefined,
        onDoubleClick: onDoubleClick ? (node: React.ReactElement) => {
            onDoubleClick(node.props.eventKey, { event: 'dblclick', node });
        } : undefined,
        setFocusKey,
        keyFun,
        children,
        cacheTreeNodes: cacheTreeNodesRef.current,
        onFocus
    });

    // ==================== 初始化 ====================
    useEffect(() => {
        // const getInitialTreeData = () => {

        //     if (lazyLoad && propTreeData) {
        //         let sliceTreeList: FlatTreeData[] = [];
        //         // 使用 expandedKeys 而不是 cacheExpandedKeys，因为 expandedKeys 已经包含了 autoExpandParent 的计算结果
        //         const expandedKeysSet = new Set(expandedKeys || []);
        //         let flatTreeData = deepTraversal({
        //             treeData: propTreeData,
        //             parentKey: null,
        //             isShown: true,
        //             expandedKeysSet,
        //             flatTreeKeysMap: flatTreeKeysMapRef.current,
        //             fieldNames,
        //             loadData: !!loadData
        //         });

        //         flatTreeData.forEach((element: FlatTreeData) => {
        //             if (sliceTreeList.length >= endIndexRef.current) return;
        //             sliceTreeList.push(element);
        //         });

        //         const result = handleTreeListChange(sliceTreeList, flatTreeKeysMapRef.current, fieldNames);
        //         return { flatTreeData, treeData: result.treeData };
        //     } else {
        //         return { treeData: propTreeData || [] };
        //     }
        // }
        // // 初始化树数据
        // const { treeData: initTreeData = [], flatTreeData: initFlatTreeData = [] } = getInitialTreeData();
        // setTreeData(initTreeData);
        // setFlatTreeData(initFlatTreeData);

        // 计算行高
        calculateRowHeight();
    }, []);

    // 提供内部对象给外部
    useEffect(() => {
        if (_getTreeObj) {
            _getTreeObj({
                checkedKeys,
                halfCheckedKeys,
                selectedKeys,
                expandedKeys,
                loadedKeys
            } as any);
        }
    }, [_getTreeObj, checkedKeys, halfCheckedKeys, selectedKeys, expandedKeys, loadedKeys]);

    // const hasTreeNode = useCallback(() => {
    //     const noTreeNode = children === null ||
    //         typeof children === 'undefined' ||
    //         (typeof children === 'object' && (children as any).length === 0) ||
    //         (typeof propTreeData === 'object' && propTreeData.length === 0);
    //     return !noTreeNode;
    // }, [children, propTreeData]);

    const calculateRowHeight = useCallback(() => {
        if (!lazyLoad) return;
        const treenodes = treeRef.current?.querySelectorAll(`.${clsPrefix}-treenode-close`)[0] as HTMLElement;
        if (!treenodes) return;
        hasCalculateRowHeightRef.current = true;
        const rowHeight = treenodes.getBoundingClientRect().height;
        storeRef.current.setState({
            rowHeight: rowHeight || 28
        });
    }, [lazyLoad, clsPrefix]);

    // ==================== 渲染相关 ====================
    const renderTreefromData = useCallback((data: TreeData[]) => {
        if (renderTreeNodes) {
            return renderTreeNodes(data);
        }

        if (!data || !Array.isArray(data)) {
            return [];
        }

        const loop = (data: TreeData[]): React.ReactElement[] => data.map((item) => {
            let { key, title, isLeaf, children, ...others } = item;
            title = fieldNames?.title ? item[fieldNames?.title] : title;
            key = (fieldNames?.key ? item[fieldNames?.key] : key) + "";
            children = fieldNames?.children ? item[fieldNames?.children] : children;

            if (children) {
                return (
                    <TreeNode
                        {...omit(others, ["children"])}
                        fieldidPrefix={fieldid ? fieldid : undefined}
                        key={key}
                        isLeaf={isLeaf}
                        ext={item}
                        title={title}
                        onRenderTitle={renderTitle}
                    >
                        {loop(children)}
                    </TreeNode>
                );
            }
            return (
                <TreeNode
                    {...others}
                    fieldidPrefix={fieldid ? fieldid : undefined}
                    key={key}
                    isLeaf={isLeaf}
                    ext={item}
                    title={title}
                    onRenderTitle={renderTitle}
                />
            );
        });

        return loop(data);
    }, [renderTreeNodes, fieldNames, fieldid, renderTitle]);

    // ==================== 方案 C：缓存优化 ====================
    // 缓存 rootMethods 对象，避免每次渲染为每个节点创建新的回调函数
    const rootMethods = useMemo(() => ({
        onExpand: (node: any, loadDataCallbackInfo?: string, event?: React.MouseEvent) => {
            const treeNode = {
                props: node.props,
                key: node.props.eventKey,
                type: TreeNode,
                getNodeChildren: node.getNodeChildren
            } as React.ReactElement;
            return hookHandleExpand(treeNode, loadDataCallbackInfo, event);
        },
        onCheck: (node: any, event?: React.MouseEvent) => {
            const treeNode = {
                props: node.props,
                key: node.props.eventKey,
                type: TreeNode,
                getNodeChildren: node.getNodeChildren
            } as React.ReactElement;
            hookHandleCheck(treeNode, event);
        },
        onSelect: (node: any, event: React.MouseEvent) => {
            const treeNode = {
                props: node.props,
                key: node.props.eventKey,
                type: TreeNode,
                getNodeChildren: node.getNodeChildren
            } as React.ReactElement;
            hookHandleSelect(treeNode, event);
        },
        onDoubleClick: onDoubleClick ? (node: React.ReactElement) => {
            onDoubleClick(node.props.eventKey, { event: 'dblclick', node });
        } : undefined,
        onMouseEnter: onMouseEnter ? (e: React.MouseEvent, node: React.ReactElement) => {
            onMouseEnter({ event: e, node });
        } : undefined,
        onMouseLeave: onMouseLeave ? (e: React.MouseEvent, node: React.ReactElement) => {
            onMouseLeave({ event: e, node });
        } : undefined,
        onContextMenu: onRightClick ? (e: React.MouseEvent, node: React.ReactElement) => {
            onRightClick({ event: e, node });
        } : undefined,
        onDragStart: handleDragStart,
        onDragEnter: handleDragEnter,
        onDragOver: handleDragOver,
        onDragLeave: handleDragLeave,
        onDrop: handleDrop,
        onDragEnd: handleDragEnd,
        onKeyDown: handleKeyDown,
    } as RootTreeMethods), [
        hookHandleExpand,
        hookHandleCheck,
        hookHandleSelect,
        onDoubleClick,
        onMouseEnter,
        onMouseLeave,
        onRightClick,
        handleDragStart,
        handleDragEnter,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handleDragEnd,
        handleKeyDown
    ]);

    // 缓存 filterTreeNode 函数
    const memoizedFilterTreeNode = useCallback((node: React.ReactElement) => {
        if (typeof props.filterTreeNode !== 'function' || node.props.disabled) {
            return false;
        }
        return props.filterTreeNode.call(null, node);
    }, [props.filterTreeNode]);

    // 缓存 onResetFocusKey 函数
    const memoizedOnResetFocusKey = useCallback(() => {
        setFocusKey('');
    }, [setFocusKey]);

    // renderTreeNode 函数
    const renderTreeNode = (child: React.ReactElement, index: number, level = 0) => {
        if (!child) return null;

        const actualIndex = index + startIndexRef.current;
        const pos = `${level}-${actualIndex}`;
        const key: string = child.key as string || pos;

        // 检查是否需要自动展开
        let shouldAutoExpand = false;
        if (autoExpandKeys && autoExpandKeys.includes(key)) {
            shouldAutoExpand = true;
        }

        // ✅ 方案 C：使用缓存的 rootMethods 对象
        // 设置 renderTreeNode 引用（避免循环依赖）
        rootMethods.renderTreeNode = renderTreeNode;

        // 为节点创建完整的 props
        const cloneProps: TreeNodeProps = {
            autoExpand: shouldAutoExpand,
            root: rootMethods,
            eventKey: key,
            pos,
            selectable: child.props.hasOwnProperty('selectable') ? child.props.selectable : selectable,
            expandOnClickNode,
            loadData,
            clsPrefix,
            showLine,
            showIcon,
            draggable: child.props.hasOwnProperty('draggable') ? child.props.draggable : draggable,
            showDraggableIcon,
            dragOver: dragOverNodeKey === key && dropPositionY === 0,
            dragOverGapTop: dragOverNodeKey === key && dropPositionY === -1,
            dragOverGapBottom: dragOverNodeKey === key && (dropPositionY === 1 || dropPositionY === 2),
            dropPositionX: dragOverNodeKey === key ? dropPositionX : undefined,
            _dropTrigger: !dragNodeHighlight,
            expanded: expandedKeysSet.has(key),
            selected: selectedKeysSet.has(key),
            focused: focusKey === key,
            filterTreeNode: memoizedFilterTreeNode,
            openIcon,
            closeIcon,
            focusable,
            tabIndexKey: selectedKeys[0],
            tabIndexValue,
            ext: child.props.ext,
            mustExpandable,
            isLeaf: child.props.hasOwnProperty('isLeaf') ? child.props.isLeaf : null,
            fieldid: child.props.fieldid,
            fieldidPrefix: fieldid,
            syncCheckedAndSelectedStatus,
            disabled: !!child?.props?.disabled || disabled,
            icon: child?.props?.icon || icon,
            isBigData: flatTreeData && flatTreeData.length > 500,
            cacheTreeNodes: cacheTreeNodesRef.current
        };

        // 处理勾选状态
        if (checkable) {
            cloneProps.checkable = checkable;
            cloneProps.inverse = inverse;
            cloneProps.getCheckboxAttrs = getCheckboxAttrs;

            if (checkStrictly) {
                // ✅ 性能优化：使用 Set.has() 替代 indexOf，复杂度从 O(m) 降低到 O(1)
                cloneProps.checked = checkedKeysSet.has(key);
                if (controlledCheckedKeys && (controlledCheckedKeys as CheckedKeysObj).halfChecked) {
                    const halfCheckedArray = (controlledCheckedKeys as CheckedKeysObj).halfChecked || [];
                    const halfCheckedSetFromControlled = new Set(halfCheckedArray);
                    cloneProps.halfChecked = halfCheckedSetFromControlled.has(key);
                } else {
                    cloneProps.halfChecked = false;
                }
            } else {
                // ✅ 性能优化：使用 Set.has() 替代 indexOf
                cloneProps.checked = checkedKeysSet.has(key);
                cloneProps.halfChecked = halfCheckedKeysSet.has(key);
            }
        }

        // 处理连接线信息（使用缓存的 treeNodesStatesKeysInfo）
        if (showLine) {
            cloneProps.treeNodesStatesKeysInfo = treeNodesStatesKeysInfo;
            cloneProps.treeNodesStates = treeNodesStates;
        }

        // ✅ 方案 C：使用缓存的 onResetFocusKey 函数
        if (focusable) {
            cloneProps.onResetFocusKey = memoizedOnResetFocusKey;
        }

        return React.cloneElement(child, cloneProps);
    };

    // 单独处理 treeNodesStates 初始化，确保在组件挂载后初始化
    useEffect(() => {
        // 初始化 treeNodesStates
        if ((showLine || draggable || checkable) && children) {
            initTreeNodesStates(children as React.ReactElement[], lazyLoad ? startIndexRef.current : 0);
        } else if ((showLine || draggable || checkable) && propTreeData) {
            const treeNode = renderTreefromData(propTreeData);
            initTreeNodesStates(treeNode, lazyLoad ? startIndexRef.current : 0);
        }

        // 拖拽模式下，初始化 flatKeyTree 和 treeElsObj
        if (draggable) {
            flatKeyTreeRef.current = [];
            treeElsObjRef.current = {};
            const treeChildren = children || renderTreefromData(propTreeData || []);
            loopAllChildren(treeChildren as React.ReactElement[], (_item: React.ReactElement, _index: number, pos: string, _keyOrPos: string) => {
                flatKeyTreeRef.current.push(_keyOrPos);
                treeElsObjRef.current[_keyOrPos] = React.cloneElement(_item, { pos, eventKey: _keyOrPos });
            });
        }
    }, [children, propTreeData, draggable, checkable, lazyLoad, initTreeNodesStates, renderTreefromData]);

    // 懒加载场景：监听 expandedKeys/数据 变化，重新拍平并更新可视 treeData（解决展开后未渲染问题）
    useEffect(() => {
        if (children) {
            loopAllChildren(children as React.ReactElement[], (item: React.ReactElement) => {
                if (item.props.disabled && item.key) {
                    disabledTreeNodesRef.current.push({ key: item.key as string });
                }
            });
        }
        if (lazyLoad && propTreeData) {
        // 根据当前展开态重建一维数据
            const expandedKeysSetLocal = new Set(expandedKeys || []);
            const newFlatTreeData = deepTraversal({
                treeData: propTreeData,
                parentKey: null,
                isShown: true,
                expandedKeysSet: expandedKeysSetLocal,
                flatTreeKeysMap: flatTreeKeysMapRef.current,
                fieldNames,
                loadData: !!loadData
            });
            setFlatTreeData(newFlatTreeData);

            // 依据当前的窗口索引截取可视数据，并更新 treeData
            const startIndex = startIndexRef.current;
            const endIndex = endIndexRef.current;
            const sliceList = newFlatTreeData.slice(startIndex, endIndex);
            const result = handleTreeListChange(sliceList, flatTreeKeysMapRef.current, fieldNames, startIndex, endIndex);
            setTreeData(result.treeData);
        } else {
            setTreeData(propTreeData || []);
        }
    }, [lazyLoad, propTreeData, expandedKeys, fieldNames, !!loadData]);

    // ==================== 渲染 ====================
    const domProps: TreeUlDomProps = {
        className: classNames(
            className,
            clsPrefix,
            `${clsPrefix}${dir === 'rtl' ? "-rtl" : ""}`,
            showLine && `${clsPrefix}-show-line`,
            blockNode && `${clsPrefix}-block-node`,
            checkable && `${clsPrefix}-checkable`,
            disabled && `${clsPrefix}-disabled`,
            expandOnClickNode && `${clsPrefix}-expand-on-click`,
            showDraggableIcon && `${clsPrefix}-show-draggable`,
        ),
        role: 'tree-node',
        style
    };

    if (focusable) {
        domProps.onFocus = handleUlFocus;
    }

    if (fieldid) {
        domProps.fieldid = fieldid;
    }

    // 准备渲染的子节点
    let treeChildren = children;
    if (!children && treeData) {
        const treeNode = renderTreefromData(treeData);
        cacheTreeNodesRef.current = treeNode;
        treeChildren = treeNode as any;
    }

    // 懒加载模式的占位高度
    let preHeight = 0;
    let sufHeight = 0;
    if (lazyLoad) {
        const rowHeight = storeRef.current.getState().rowHeight;
        preHeight = getSumHeight(0, startIndexRef.current, rowHeight);
        sufHeight = getSumHeight(endIndexRef.current, flatTreeData.length, rowHeight);
    }

    const customProps = omit(otherProps as any, [
        'showIcon', 'onLoad', 'cancelUnSelect', 'autoExpandParent',
        'defaultExpandAll', 'autoSelectWhenFocus', 'renderTreeNodes',
        'mustExpandable', 'onFocus', 'getCheckboxAttrs', 'inverse',
        'syncCheckedAndSelectedStatus', 'dragDelayExpandTime'
    ]);

    const adapterNid = getNid(props);

    return lazyLoad ? (
        <InfiniteScroll
            className={`${clsPrefix}-infinite-scroll`}
            treeList={flatTreeData}
            debounceDuration={debounceDuration || 150}
            height={height}
            isFold={isFold}
            handleScroll={(treeList: FlatTreeData[], startIndex?: number, endIndex?: number) => {
                // 先更新 ref，确保渲染时能读到正确的值

                const result = handleTreeListChange(treeList, flatTreeKeysMapRef.current, fieldNames, startIndex, endIndex);
                startIndexRef.current = result.startIndex;
                endIndexRef.current = result.endIndex;
                setTreeData(result.treeData);
            }}
            getScrollParent={getScrollContainer}
            store={storeRef.current}
        >
            <ul
                {...domProps}
                {...customProps}
                {...adapterNid}
                ref={treeRef}
                tabIndex={focusable ? tabIndexValue : undefined}
            >
                <li style={{ height: preHeight }} className={`${clsPrefix}node-start`} key={'tree_node_start'}></li>
                {React.Children.map(treeChildren, renderTreeNode)}
                <li style={{ height: sufHeight }} className={`${clsPrefix}node-end`} key={'tree_node_end'}></li>
            </ul>
        </InfiniteScroll>
    ) : (
        <ul
            {...domProps}
            {...customProps}
            {...adapterNid}
            ref={treeRef}
            tabIndex={focusable ? tabIndexValue : undefined}
        >
            {React.Children.map(treeChildren, renderTreeNode)}
        </ul>
    );
};

// 应用装饰器
// 注意：defaultProps 已在组件顶部定义，这里传递给 WebUI 装饰器是为了保持框架的一致性
// WebUI 装饰器可能会处理其他全局配置，如主题、CSS前缀等
export default WithConfigConsumer({name: "tree"})(
    WebUI({name: "tree", defaultProps})(TreeFunctional)
);